<template>
  <div
    class="relative w-full pt-2 z-10"
    v-click-outside="clickOutside"
  >
    <div class="relative">
      <div class="label-primary flex gap-1.5 relative z-10">
        <span>{{ changeCase(label, ElementE.LABEL) }}</span>
      </div>

      <div class="relative">
        <input
          ref="inputRef"
          v-model="inputValue"
          class="h-[46px] p-3 border border-gray-300 rounded-lg text-sm text-neutral-125 w-full z-5"
          :class="[
            isOptionsListVisible && 'border-red-500',
            errors.customValue && 'border-critical'
          ]"
          :type="validateAsNumber ? 'number' : 'text'"
          :min="validateAsNumber ? min : undefined"
          :max="validateAsNumber ? max : undefined"
          :placeholder="placeholder"
          @input="onInput"
          @focus="onFocus"
          @blur="onBlur"
          @keydown.enter="onEnter"
          @keydown.escape="onEscape"
          @keydown.arrow-down="onArrowDown"
          @keydown.arrow-up="onArrowUp"
          @keypress="onKeypress"
        />

        <!-- Dropdown list positioned relative to input container -->
        <ul
          v-if="isOptionsListVisible && filteredValues.length > 0"
          class="absolute w-full top-full mt-1 border border-gray-300 rounded-lg bg-white shadow-lg z-10"
          :class="
            filteredValues.length > 3 ? 'h-[180px] overflow-y-scroll' : 'overflow-hidden'
          "
        >
          <li
            v-for="(item, index) in filteredValues"
            :key="item.id"
            :ref="'option-' + index"
            class="p-3 text-sm text-neutral-125 cursor-pointer hover:bg-neutral-0"
            :class="[
              String(item.name) === inputValue ? 'bg-neutral-25' : 'bg-white',
              index === highlightedIndex && 'bg-neutral-25'
            ]"
            @mousedown.prevent="selectItem(item)"
            @mouseenter="highlightedIndex = index"
          >
            <span>{{ item.name }}</span>
          </li>
        </ul>
      </div>

      <div v-if="errors.customValue" class="mt-2">
        <p class="text-xs text-critical-main">
          {{ errors.customValue }}
        </p>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { changeCase } from "../../helpers";
import { ElementE } from '../../enums';
import { IShowDay } from "../edittabs/constants";

interface ErrorI {
  customValue: string;
}

export default defineComponent({
  name: "EditableDropdown",
  components: { },
  props: {
    label: { type: String, required: true },
    placeholder: { type: String, default: "Enter value or select from list" },
    values: {
      type: Array as () => IShowDay[],
      default: () => [] as IShowDay[],
    },
    defaultValue: { type: String, default: "" },
    min: { type: Number, default: 1 },
    max: { type: Number, default: 365 },
    validateAsNumber: { type: Boolean, default: false },
  },
  emits: ['select', 'input'],
  data() {
    return {
      isOptionsListVisible: false,
      inputValue: this.defaultValue,
      highlightedIndex: -1,
      errors: {
        customValue: "",
      } as ErrorI,
      changeCase: changeCase,
      ElementE: ElementE,
    };
  },
  computed: {
    filteredValues() {
      if (!this.inputValue) {
        return this.values;
      }
      const searchValue = String(this.inputValue).toLowerCase();
      return this.values.filter(item => 
        String(item.name).toLowerCase().includes(searchValue)
      );
    },
  },
  watch: {
    defaultValue: {
      handler(newValue) {
        if (newValue !== this.inputValue) {
          this.inputValue = newValue;
        }
      },
      immediate: true
    },
  },
  methods: {
    clickOutside() {
      this.isOptionsListVisible = false;
      this.highlightedIndex = -1;
    },
    onInput() {
      this.isOptionsListVisible = true;
      this.highlightedIndex = -1;
      this.validateInput();
      
      // Emit immediately for real-time updates (backwards compatibility)
      if (this.validateAsNumber) {
        const numValue = parseInt(this.inputValue);
        if (!isNaN(numValue) && numValue >= this.min && numValue <= this.max) {
          this.$emit('select', numValue);
        }
      } else {
        this.$emit('select', this.inputValue);
      }
      
      this.$emit('input', this.inputValue);
    },
    onFocus() {
      this.isOptionsListVisible = true;
    },
    onBlur() {
      // Delay hiding to allow for click events on dropdown items
      setTimeout(() => {
        this.isOptionsListVisible = false;
        this.highlightedIndex = -1;
        this.validateInput();
        
        // Final validation and emit on blur
        if (this.validateAsNumber) {
          const numValue = parseInt(this.inputValue);
          if (!isNaN(numValue) && numValue >= this.min && numValue <= this.max) {
            this.$emit('select', numValue);
          }
        } else if (this.inputValue) {
          this.$emit('select', this.inputValue);
        }
      }, 150);
    },
    onEnter() {
      if (this.highlightedIndex >= 0 && this.filteredValues.length > 0) {
        this.selectItem(this.filteredValues[this.highlightedIndex]);
      } else {
        this.handleCustomValue();
      }
    },
    onEscape() {
      this.isOptionsListVisible = false;
      this.highlightedIndex = -1;
    },
    onArrowDown() {
      if (!this.isOptionsListVisible) {
        this.isOptionsListVisible = true;
        return;
      }
      this.highlightedIndex = Math.min(
        this.highlightedIndex + 1,
        this.filteredValues.length - 1
      );
    },
    onArrowUp() {
      if (!this.isOptionsListVisible) {
        this.isOptionsListVisible = true;
        return;
      }
      this.highlightedIndex = Math.max(this.highlightedIndex - 1, 0);
    },
    onKeypress(event: KeyboardEvent) {
      // If validating as number, only allow numeric input
      if (this.validateAsNumber) {
        const char = String.fromCharCode(event.which);
        if (!/[0-9]/.test(char)) {
          event.preventDefault();
        }
      }
    },
    selectItem(item: IShowDay) {
      this.inputValue = String(item.name);
      this.isOptionsListVisible = false;
      this.highlightedIndex = -1;
      this.errors.customValue = "";
      
      // Force update the input element value
      this.$nextTick(() => {
        if (this.$refs.inputRef) {
          (this.$refs.inputRef as HTMLInputElement).value = String(item.name);
        }
      });
      
      this.$emit('select', item.name);
    },
    handleCustomValue() {
      this.validateInput();
      if (!this.errors.customValue) {
        this.$emit('select', this.validateAsNumber ? parseInt(this.inputValue) : this.inputValue);
      }
    },
    validateInput() {
      if (this.validateAsNumber) {
        const numValue = parseInt(this.inputValue);
        if (isNaN(numValue)) {
          this.errors.customValue = "Please enter a valid number";
        } else if (numValue < this.min || numValue > this.max) {
          this.errors.customValue = `Value must be between ${this.min} and ${this.max}`;
        } else {
          this.errors.customValue = "";
        }
      } else {
        this.errors.customValue = "";
      }
    },
  },
});
</script>

<style scoped>
/* Remove all default focus styling to match DropdownMenu behavior */
input:focus {
  outline: none !important;
  box-shadow: none !important;
  border-color: #d1d5db !important; /* Keep original border color */
}

/* When dropdown is open, show red border like DropdownMenu */
input:focus.border-red-500 {
  border-color: #ef4444 !important;
}

/* Style number input arrows to always be visible */
input[type="number"] {
  -moz-appearance: auto;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  opacity: 1;
  height: auto;
}

/* Suggestion list styling */
ul {
  max-height: 240px;
}

ul li {
  transition: background-color 0.15s ease;
}

ul li:hover {
  background-color: #f9fafb;
}

ul li.bg-neutral-25 {
  background-color: #f3f4f6;
}
</style> 